import { apiClient } from '../../../lib/api-client';

export type StockTakeStatus = 'OPEN' | 'REVIEW' | 'CLOSED' | 'CANCELLED';

export type StockTakeResolution = 'LOST' | 'THEFT' | 'MISS_OUT' | 'MISS_IN' | 'ERROR' | 'OTHER';

export interface StockTakeResponse {
  id: number;
  warehouseId: number;
  warehouseName: string;
  status: StockTakeStatus;
  openedBy?: number;
  openedByName?: string;
  openedAt?: string;
  closedBy?: number;
  closedByName?: string;
  closedAt?: string;
  note?: string;
  createdAt: string;
  totalLines: number;
  scannedLines: number;
  discrepancyLines: number;
}

export interface StockTakeLineResponse {
  id: number;
  skuId: number;
  skuNo?: string;
  upcCode?: string;
  productName?: string;
  brand?: string;
  color?: string;
  size?: string;
  systemQty: number;
  scannedQty: number;
  discrepancy: number;
  resolution?: StockTakeResolution;
  note?: string;
}

export interface StockTakeOpenRequest {
  warehouseId: number;
  note?: string;
}

export interface StockTakeScanRequest {
  upcCode: string;
}

export interface StockTakeLineUpdateRequest {
  resolution?: StockTakeResolution;
  note?: string;
  scannedQty?: number;
}

export interface Paged<T> {
  list: T[];
  total: number;
  page: number;
  size: number;
}

export const stockTakeApi = {
  list: async (page = 0, size = 20, warehouseId?: number): Promise<Paged<StockTakeResponse>> => {
    const res = await apiClient.get('/stock-takes', { params: { page, size, warehouseId } });
    return res.data.data;
  },
  listActive: async (): Promise<StockTakeResponse[]> => {
    const res = await apiClient.get('/stock-takes/active');
    return res.data.data;
  },
  get: async (id: number): Promise<StockTakeResponse> => {
    const res = await apiClient.get(`/stock-takes/${id}`);
    return res.data.data;
  },
  open: async (body: StockTakeOpenRequest): Promise<StockTakeResponse> => {
    const res = await apiClient.post('/stock-takes', body);
    return res.data.data;
  },
  scan: async (id: number, body: StockTakeScanRequest): Promise<StockTakeLineResponse> => {
    const res = await apiClient.post(`/stock-takes/${id}/scan`, body);
    return res.data.data;
  },
  lines: async (id: number, onlyDiscrepancy = false): Promise<StockTakeLineResponse[]> => {
    const res = await apiClient.get(`/stock-takes/${id}/lines`, { params: { onlyDiscrepancy } });
    return res.data.data;
  },
  toReview: async (id: number): Promise<StockTakeResponse> => {
    const res = await apiClient.post(`/stock-takes/${id}/review`);
    return res.data.data;
  },
  updateLine: async (id: number, lineId: number, body: StockTakeLineUpdateRequest): Promise<StockTakeLineResponse> => {
    const res = await apiClient.patch(`/stock-takes/${id}/lines/${lineId}`, body);
    return res.data.data;
  },
  close: async (id: number): Promise<StockTakeResponse> => {
    const res = await apiClient.post(`/stock-takes/${id}/close`);
    return res.data.data;
  },
  cancel: async (id: number): Promise<StockTakeResponse> => {
    const res = await apiClient.post(`/stock-takes/${id}/cancel`);
    return res.data.data;
  },
  exportExcel: async (id: number, onlyDiscrepancy = false): Promise<void> => {
    const res = await apiClient.get(`/stock-takes/${id}/export`, {
      params: { onlyDiscrepancy },
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([res.data]));
    const link = document.createElement('a');
    link.href = url;
    const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    link.setAttribute('download', `stock-take-${id}-${today}.xlsx`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};
